// Firestore Database Service
import {
    collection,
    doc,
    addDoc,
    getDoc,
    getDocs,
    updateDoc,
    deleteDoc,
    query,
    where,
    orderBy,
    serverTimestamp,
    arrayUnion,
    arrayRemove
} from 'firebase/firestore';
import { db } from './config.js';

// ============ SITES ============

/**
 * Create a new excavation site
 * @param {Object} siteData 
 * @returns {Promise<string>} Site ID
 */
export async function createSite(siteData) {
    const docRef = await addDoc(collection(db, 'sites'), {
        ...siteData,
        shares: [], // List of emails
        permissions: {}, // Map of email -> permission object
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
    });
    return docRef.id;
}

/**
 * Get all sites for a user (owned + shared)
 * @param {string} userId 
 * @param {string} userEmail
 * @returns {Promise<Array>}
 */
export async function getSites(userId, userEmail) {
    const sites = [];

    // 1. Get owned sites
    const ownedQuery = query(
        collection(db, 'sites'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
    );
    const ownedSnap = await getDocs(ownedQuery);
    ownedSnap.forEach(doc => {
        sites.push({ id: doc.id, ...doc.data(), role: 'owner' });
    });

    // 2. Get shared sites (if email provided)
    // 2. Get shared sites (if email provided)
    if (userEmail) {
        try {
            const sharedQuery = query(
                collection(db, 'sites'),
                where('shares', 'array-contains', userEmail)
            );
            const sharedSnap = await getDocs(sharedQuery);
            sharedSnap.forEach(doc => {
                // Avoid duplicates if user is both owner and shared
                if (!sites.some(s => s.id === doc.id)) {
                    // Get permissions for this user
                    const data = doc.data();
                    const userPermissions = data.permissions ? data.permissions[userEmail] : {};

                    sites.push({
                        id: doc.id,
                        ...data,
                        role: 'member',
                        userPermissions
                    });
                }
            });
        } catch (error) {
            console.warn('Error fetching shared sites:', error);
            // Continue execution to show at least owned sites
        }
    }

    // Client-side sort by createdAt desc (needed because we merged two queries)
    return sites.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(0);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(0);
        return dateB - dateA;
    });
}

/**
 * Invite a user to a site
 * @param {string} siteId 
 * @param {string} email 
 * @param {Object} permissions 
 */
export async function inviteUserToSite(siteId, email, permissions) {
    const siteRef = doc(db, 'sites', siteId);

    // Fetch-modify-write pattern ensures we don't accidentally wipe other permissions
    // and handles the dynamic key nature safely
    const snap = await getDoc(siteRef);
    if (!snap.exists()) throw new Error('Site not found');

    const currentData = snap.data();
    const currentPermissions = currentData.permissions || {};
    currentPermissions[email] = permissions;

    // Also ensure email is in shares array for querying
    const currentShares = currentData.shares || [];
    if (!currentShares.includes(email)) {
        currentShares.push(email);
    }

    await updateDoc(siteRef, {
        shares: currentShares,
        permissions: currentPermissions,
        updatedAt: serverTimestamp()
    });
}

/**
 * Remove a user from a site
 * @param {string} siteId 
 * @param {string} email 
 */
export async function removeUserFromSite(siteId, email) {
    const siteRef = doc(db, 'sites', siteId);

    const snap = await getDoc(siteRef);
    if (!snap.exists()) throw new Error('Site not found');

    const currentData = snap.data();
    const currentPermissions = currentData.permissions || {};
    delete currentPermissions[email];

    await updateDoc(siteRef, {
        shares: arrayRemove(email),
        permissions: currentPermissions,
        updatedAt: serverTimestamp()
    });
}


/**
 * Update user permissions
 * @param {string} siteId 
 * @param {string} email 
 * @param {Object} permissions 
 */
export async function updateUserPermissions(siteId, email, permissions) {
    const siteRef = doc(db, 'sites', siteId);

    const snap = await getDoc(siteRef);
    if (!snap.exists()) throw new Error('Site not found');

    const currentData = snap.data();
    const currentPermissions = currentData.permissions || {};
    currentPermissions[email] = permissions;

    await updateDoc(siteRef, {
        permissions: currentPermissions,
        updatedAt: serverTimestamp()
    });
}

/**
 * Get a single site by ID
 * @param {string} siteId 
 * @returns {Promise<Object|null>}
 */
export async function getSite(siteId) {
    const docSnap = await getDoc(doc(db, 'sites', siteId));
    if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
}

/**
 * Update a site
 * @param {string} siteId 
 * @param {Object} data 
 * @returns {Promise<void>}
 */
export async function updateSite(siteId, data) {
    await updateDoc(doc(db, 'sites', siteId), {
        ...data,
        updatedAt: serverTimestamp()
    });
}

/**
 * Delete a site
 * @param {string} siteId 
 * @returns {Promise<void>}
 */
export async function deleteSite(siteId) {
    await deleteDoc(doc(db, 'sites', siteId));
}

// ============ LOG ENTRIES ============

/**
 * Create a new log entry
 * @param {string} siteId 
 * @param {Object} entryData 
 * @returns {Promise<string>} Entry ID
 */
export async function createLogEntry(siteId, entryData) {
    const docRef = await addDoc(collection(db, 'sites', siteId, 'logs'), {
        ...entryData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
    });
    return docRef.id;
}

/**
 * Get all log entries for a site
 * @param {string} siteId 
 * @returns {Promise<Array>}
 */
export async function getLogEntries(siteId) {
    const q = query(
        collection(db, 'sites', siteId, 'logs'),
        orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

/**
 * Get a single log entry
 * @param {string} siteId 
 * @param {string} entryId 
 * @returns {Promise<Object|null>}
 */
export async function getLogEntry(siteId, entryId) {
    const docSnap = await getDoc(doc(db, 'sites', siteId, 'logs', entryId));
    if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
}

/**
 * Update a log entry
 * @param {string} siteId 
 * @param {string} entryId 
 * @param {Object} data 
 * @returns {Promise<void>}
 */
export async function updateLogEntry(siteId, entryId, data) {
    await updateDoc(doc(db, 'sites', siteId, 'logs', entryId), {
        ...data,
        updatedAt: serverTimestamp()
    });
}

/**
 * Delete a log entry
 * @param {string} siteId 
 * @param {string} entryId 
 * @returns {Promise<void>}
 */
export async function deleteLogEntry(siteId, entryId) {
    await deleteDoc(doc(db, 'sites', siteId, 'logs', entryId));
}

// ============ 3D ASSETS ============

/**
 * Generate unique artifact ID for a site
 * @param {string} siteId 
 * @returns {Promise<number>} Unique artifact ID (0-100000)
 */
async function generateUniqueArtifactId(siteId) {
    const existingAssets = await getAssets(siteId);
    const existingIds = new Set(existingAssets.map(a => a.artifactId).filter(id => id !== undefined));

    let artifactId;
    let attempts = 0;
    const maxAttempts = 100;

    do {
        artifactId = Math.floor(Math.random() * 100001); // 0-100000
        attempts++;
        if (attempts > maxAttempts) {
            throw new Error('Unable to generate unique artifact ID after maximum attempts');
        }
    } while (existingIds.has(artifactId));

    return artifactId;
}

/**
 * Create a new 3D asset record
 * @param {string} siteId 
 * @param {Object} assetData 
 * @returns {Promise<string>} Asset ID
 */
export async function createAsset(siteId, assetData) {
    // Generate unique artifact ID
    const artifactId = await generateUniqueArtifactId(siteId);

    const docRef = await addDoc(collection(db, 'sites', siteId, 'assets'), {
        ...assetData,
        artifactId,
        createdAt: serverTimestamp()
    });
    return docRef.id;
}

/**
 * Get all assets for a site
 * @param {string} siteId 
 * @returns {Promise<Array>}
 */
export async function getAssets(siteId) {
    const q = query(
        collection(db, 'sites', siteId, 'assets'),
        orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

/**
 * Get a single asset
 * @param {string} siteId 
 * @param {string} assetId 
 * @returns {Promise<Object|null>}
 */
export async function getAsset(siteId, assetId) {
    const docSnap = await getDoc(doc(db, 'sites', siteId, 'assets', assetId));
    if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
}

/**
 * Update an asset
 * @param {string} siteId 
 * @param {string} assetId 
 * @param {Object} data 
 * @returns {Promise<void>}
 */
export async function updateAsset(siteId, assetId, data) {
    await updateDoc(doc(db, 'sites', siteId, 'assets', assetId), data);
}

/**
 * Delete an asset
 * @param {string} siteId 
 * @param {string} assetId 
 * @returns {Promise<void>}
 */
export async function deleteAsset(siteId, assetId) {
    await deleteDoc(doc(db, 'sites', siteId, 'assets', assetId));
}

// ============ FOLDERS ============

/**
 * Create a new folder
 * @param {string} siteId 
 * @param {string} name 
 * @returns {Promise<string>} Folder ID
 */
export async function createFolder(siteId, name) {
    const docRef = await addDoc(collection(db, 'sites', siteId, 'folders'), {
        name,
        createdAt: serverTimestamp()
    });
    return docRef.id;
}

/**
 * Get all folders for a site
 * @param {string} siteId 
 * @returns {Promise<Array>}
 */
export async function getFolders(siteId) {
    try {
        const q = query(
            collection(db, 'sites', siteId, 'folders'),
            orderBy('createdAt', 'asc')
        );
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
        console.warn('Error fetching folders:', error);
        return [];
    }
}

/**
 * Delete a folder (and move contents to root)
 * @param {string} siteId 
 * @param {string} folderId 
 */
export async function deleteFolder(siteId, folderId) {
    // 1. Move all assets in this folder to root (folderId = null)
    const assetsQuery = query(
        collection(db, 'sites', siteId, 'assets'),
        where('folderId', '==', folderId)
    );
    const snapshot = await getDocs(assetsQuery);

    const batchPromises = snapshot.docs.map(doc =>
        updateDoc(doc.ref, { folderId: null })
    );
    await Promise.all(batchPromises);

    // 2. Delete the folder
    await deleteDoc(doc(db, 'sites', siteId, 'folders', folderId));
}

/**
 * Move asset to a folder
 * @param {string} siteId 
 * @param {string} assetId 
 * @param {string|null} folderId 
 */
export async function updateAssetFolder(siteId, assetId, folderId) {
    await updateDoc(doc(db, 'sites', siteId, 'assets', assetId), {
        folderId: folderId
    });
}
// ============ PHOTOGRAMMETRY JOBS ============

/**
 * Create a new photogrammetry job
 * @param {string} siteId 
 * @param {string} folderId 
 * @param {string} folderName 
 * @returns {Promise<string>} Job ID
 */
export async function createPhotogrammetryJob(siteId, folderId, folderName, photoCount, creditCost) {
    const docRef = await addDoc(collection(db, 'jobs'), {
        siteId,
        folderId,
        folderName,
        photoCount,
        creditCost,
        status: 'PENDING',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
    });
    return docRef.id;
}
